import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Link, Tooltip } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetMyDailyHours } from '../redux/sagas/requests/reports';
import { buildDayColumns, backfillDayKeys, overTargetDayKeys } from '../components/RunnitDailyHours';
import { toLocalDay } from '../utils/dateUtils';

const TimeDisplay = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1.5, 3, 0.5, 3),
}));

const TimeValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h3.fontSize,
    color: theme.palette.primary.main,
    lineHeight: 1,
}));

const TimeLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
}));

// Three tabs and a remove button share the header with the title at the widget's minimum width,
// so the labels are sized to leave the title room rather than crowd it out.
const TabLabel = styled(Tab)(({ theme }) => ({
    minHeight: theme.spacing(4),
    padding: theme.spacing(0, 1, 1, 1),
    minWidth: 0,
    fontSize: theme.typography.size.xs,
}));

const PeriodTabs = styled(Tabs)({
    minHeight: '36px',
});

const DistributionRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(0, 2),
}));

const Stat = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const StatValue = styled(Typography, { shouldForwardProp: (prop) => prop !== '$tone' })(({ theme, $tone }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h6.fontSize,
    lineHeight: 1.2,
    color: $tone ? theme.palette[$tone].main : theme.palette.text.primary,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    textAlign: 'center',
}));

const FooterLink = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 2, 0.5, 2),
}));

const formatTime = (minutes) => {
    if (!minutes && minutes !== 0) return '0:00';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}:${mins.toString().padStart(2, '0')}`;
};

const monthRange = (offset) => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
    return { start: toLocalDay(start), end: toLocalDay(end) };
};

const minutesBetween = (dailyMinutes, from, to) =>
    Object.entries(dailyMinutes || {}).reduce((sum, [day, minutes]) => (day >= from && day <= to ? sum + minutes : sum), 0);

const MyTimeWidget = ({ widgetId, config = {}, onRemove }) => {
    const userId = useSelector(state => state.auth.userId);
    const [activeTab, setActiveTab] = useState(0);

    const thisMonth = useMemo(() => monthRange(0), []);
    const lastMonth = useMemo(() => monthRange(-1), []);

    // One fetch spanning both months answers every tab — the totals are summed from the same
    // per-day minutes the gap and overtime counts are read from, so they cannot disagree.
    const { data: dailyRow, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetMyDailyHours(lastMonth.start, thisMonth.end),
        cacheKey: `my-daily-hours-${lastMonth.start}-${thisMonth.end}`,
        enabled: !!userId,
    });

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const isLastMonth = activeTab === 2;
    const period = isLastMonth ? lastMonth : thisMonth;

    const distribution = useMemo(() => {
        if (!dailyRow) return null;
        const days = buildDayColumns(period.start, period.end);
        return {
            missing: backfillDayKeys(dailyRow, days).length,
            over: overTargetDayKeys(dailyRow, days).length,
            target: dailyRow.expectedDailyHours,
        };
    }, [dailyRow, period.start, period.end]);

    const currentTime = useMemo(() => {
        if (!dailyRow) return 0;
        if (activeTab === 0) {
            const today = toLocalDay();
            return dailyRow.dailyMinutes?.[today] || 0;
        }
        return minutesBetween(dailyRow.dailyMinutes, period.start, period.end);
    }, [dailyRow, activeTab, period.start, period.end]);

    const getPeriodLabel = () => {
        switch (activeTab) {
            case 0: return 'Today';
            case 1: return 'This Month';
            case 2: return 'Last Month';
            default: return '';
        }
    };

    const tabActions = (
        <PeriodTabs
            value={activeTab}
            onChange={handleTabChange}
        >
            <TabLabel label="Today" />
            <TabLabel label="This Month" />
            <TabLabel label="Last Month" />
        </PeriodTabs>
    );

    const monthWord = isLastMonth ? 'last month' : 'this month';

    return (
        <BaseWidget
            title="My Time"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
            actions={tabActions}
        >
            <TimeDisplay>
                <AccessTime fontSize='large' color="primary" />
                <TimeValue>{formatTime(currentTime)}</TimeValue>
                <TimeLabel>{getPeriodLabel()}</TimeLabel>
            </TimeDisplay>
            {distribution && (
                <DistributionRow>
                    <Tooltip title={`Weekdays ${monthWord} that have passed with no time logged, outside approved time off and company holidays`}>
                        <Stat>
                            <StatValue $tone={distribution.missing ? 'error' : undefined}>{distribution.missing}</StatValue>
                            <StatLabel>days not logged</StatLabel>
                        </Stat>
                    </Tooltip>
                    <Tooltip title={`Days ${monthWord} with more than your ${distribution.target}h daily target`}>
                        <Stat>
                            <StatValue $tone={distribution.over ? 'warning' : undefined}>{distribution.over}</StatValue>
                            <StatLabel>days over target</StatLabel>
                        </Stat>
                    </Tooltip>
                </DistributionRow>
            )}
            <FooterLink>
                <Link component={RouterLink} to={isLastMonth ? '/my-hours?month=last' : '/my-hours'} variant="body2">
                    View my hours day by day
                </Link>
            </FooterLink>
        </BaseWidget>
    );
};

export default MyTimeWidget;
